<template>
  <div class="prompt-management">
    <ds-header title="提示词管理"></ds-header>

    <div class="prompt-layout">
      <!-- 左侧模板列表 -->
      <div class="template-sidebar">
        <div class="sidebar-header">
          <span class="sidebar-title">模板列表 (共 {{ pagination.total }} 个)</span>
          <a-button type="primary" size="small" @click="handleAddTemplate">
            <a-icon type="plus" />新增
          </a-button>
        </div>

        <div class="sidebar-filters">
          <a-input
            v-model.trim="searchKeyword"
            placeholder="搜索模板..."
            allow-clear
            class="search-input"
            @pressEnter="handleSearch"
          >
            <a-icon slot="prefix" type="search" />
          </a-input>
          <a-select
            v-model="filterType"
            placeholder="全部类型"
            class="type-select"
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            @change="handleSearch"
          >
            <a-select-option v-for="item in typeOptions" :key="item.value" :value="item.value">
              {{ item.label }}
            </a-select-option>
          </a-select>
        </div>

        <a-spin :spinning="listLoading" class="template-list-spin">
          <div class="template-list">
            <div
              v-for="item in templates"
              :key="item.id"
              class="template-item"
              :class="{ 'template-item--active': selectedTemplateId === item.id }"
              @click="selectTemplate(item.id)"
            >
              <div class="template-item__header">
                <span class="template-item__name">{{ item.name }}</span>
                <a-tag :color="item.enabled ? 'green' : 'red'" class="template-item__status">
                  {{ item.enabled ? '启用' : '禁用' }}
                </a-tag>
              </div>
              <div class="template-item__desc">{{ item.description || '-' }}</div>
              <div class="template-item__meta">
                <span class="template-item__key">{{ item.key }}</span>
                <span class="template-item__category">
                  <a-icon type="folder" />{{ item.category }}
                </span>
              </div>
            </div>
            <a-empty v-if="!listLoading && !templates.length" description="暂无模板" class="template-empty" />
          </div>
        </a-spin>

        <div v-if="pagination.total" class="sidebar-pagination">
          <a-pagination
            simple
            size="small"
            :current="pagination.current"
            :page-size="pagination.pageSize"
            :total="pagination.total"
            @change="handlePageChange"
          />
        </div>
      </div>

      <!-- 右侧详情 -->
      <div class="template-detail">
        <template v-if="selectedTemplate">
          <div class="detail-header">
            <div class="detail-header__info">
              <div class="detail-header__title-row">
                <h2 class="detail-title">{{ selectedTemplate.name }}</h2>
                <a-tag :color="selectedTemplate.enabled ? 'green' : 'red'">
                  {{ selectedTemplate.enabled ? '已启用' : '已禁用' }}
                </a-tag>
                <a-tag v-if="selectedTemplate.currentVersionId" color="purple">
                  当前版本ID: {{ selectedTemplate.currentVersionId }}
                </a-tag>
              </div>
              <div class="detail-header__meta">
                <span class="meta-item">
                  <a-icon type="key" />{{ selectedTemplate.key }}
                </span>
                <span class="meta-item">
                  <a-icon type="folder" />{{ selectedTemplate.category }}
                </span>
                <span class="meta-item">
                  <a-icon type="file-text" />{{ formatPromptType(selectedTemplate.promptType) }}
                </span>
                <span class="meta-item">
                  <a-icon type="clock-circle" />{{ selectedTemplate.updateTime }}
                </span>
              </div>
            </div>
            <div class="detail-header__actions">
              <a-button type="primary" @click="handleAddVersion">
                <a-icon type="plus" />新增版本
              </a-button>
              <a-button @click="handleEditTemplate">编辑模板</a-button>
              <a-button @click="handleToggleTemplate">
                {{ selectedTemplate.enabled ? '禁用' : '启用' }}
              </a-button>
              <a-button type="link" class="danger-btn" @click="handleDeleteTemplate">
                删除模板
              </a-button>
            </div>
          </div>

          <div class="detail-description">
            <a-icon type="file-text" class="desc-icon" />
            <span>{{ selectedTemplate.description }}</span>
          </div>

          <div class="version-section">
            <div class="version-section__title">
              <a-icon type="inbox" />
              版本历史 ({{ versionPagination.total }})
            </div>

            <a-spin :spinning="versionLoading">
              <div
                v-for="version in selectedTemplate.versions"
                :key="version.id"
                class="version-card"
              >
                <div class="version-card__header">
                  <div class="version-card__header-left">
                    <span class="version-number">{{ formatVersionNo(version.versionNo) }}</span>
                    <a-tag
                      v-if="isCurrentVersion(version)"
                      color="green"
                    >
                      当前版本
                    </a-tag>
                    <a-tag
                      v-else
                      :color="getVersionStatusColor(version.versionStatus)"
                    >
                      {{ version.versionStatusDesc || formatVersionStatus(version.versionStatus) }}
                    </a-tag>
                  </div>
                  <span class="version-card__time">{{ version.updateTime || version.createTime }}</span>
                </div>

                <div class="version-card__meta">
                  <span class="meta-item">
                    <a-icon type="user" />{{ version.creator || '-' }}
                  </span>
                  <span v-if="version.publisher || version.publishTime" class="meta-item">
                    <a-icon type="rocket" />{{ version.publisher || '-' }}
                    <template v-if="version.publishTime"> · {{ version.publishTime }}</template>
                  </span>
                  <span v-if="version.remark" class="version-card__remark">{{ version.remark }}</span>
                </div>

                <div class="version-card__content">
                  <pre class="prompt-content">{{ version.promptContent }}</pre>
                </div>

                <div v-if="version.variables && version.variables.length" class="version-card__variables">
                  <span class="variables-label">变量:</span>
                  <a-tag
                    v-for="variable in version.variables"
                    :key="variable.name"
                    color="blue"
                    class="variable-tag"
                  >
                    {{ variable.name }}<span v-if="variable.required" class="required-mark">*</span>
                  </a-tag>
                </div>

                <div class="version-card__actions">
<!--                  <a-button type="link" size="small" @click="handleTestRecords(version)">-->
<!--                    <a-icon type="experiment" />测试记录-->
<!--                  </a-button>-->
                  <a-button type="link" size="small" @click="handleEditVersion(version)">
                    <a-icon type="edit" />编辑
                  </a-button>
                  <a-button type="link" size="small" class="danger-btn" @click="handleDeleteVersion(version)">
                    <a-icon type="delete" />删除
                  </a-button>
                </div>
              </div>

              <a-empty
                v-if="!versionLoading && !selectedTemplate.versions.length"
                description="暂无版本"
                class="version-empty"
              />
            </a-spin>

            <div v-if="versionPagination.total > versionPagination.pageSize" class="version-pagination">
              <a-pagination
                size="small"
                :current="versionPagination.current"
                :page-size="versionPagination.pageSize"
                :total="versionPagination.total"
                @change="handleVersionPageChange"
              />
            </div>
          </div>
        </template>

        <a-empty v-else description="请选择一个模板查看详情" class="detail-empty" />
      </div>
    </div>

    <a-modal
      :maskClosable="false"
      :title="templateModalTitle"
      :visible="templateModalVisible"
      :confirm-loading="confirmLoading"
      ok-text="确认保存"
      cancel-text="取消"
      width="720px"
      destroy-on-close
      @ok="handleTemplateModalOk"
      @cancel="handleTemplateModalCancel"
    >
      <a-form-model
        ref="templateForm"
        :model="templateForm"
        :rules="templateRules"
        :label-col="{ span: 7 }"
        :wrapper-col="{ span: 17 }"
        class="template-modal-form"
      >
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item
              label="业务Key"
              prop="key"
              extra="全局唯一，用于代码内引用"
            >
              <a-input
                v-model.trim="templateForm.key"
                placeholder="如：customer_service_main"
                allow-clear
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="模板名称" prop="name">
              <a-input
                v-model.trim="templateForm.name"
                placeholder="如：智能客服主提示词"
                allow-clear
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item label="提示词类型" prop="promptType">
              <a-select
                v-model="templateForm.promptType"
                placeholder="请选择提示词类型"
                style="width: 100%"
              >
                <a-select-option
                  v-for="item in promptTypeOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="业务分类" prop="category">
              <a-input
                v-model.trim="templateForm.category"
                placeholder="如：客服、售前、售后"
                allow-clear
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="24">
            <a-form-model-item
              label="描述"
              prop="description"
              :label-col="{ span: 3 }"
              :wrapper-col="{ span: 21 }"
            >
              <a-textarea
                v-model="templateForm.description"
                placeholder="请输入模板描述及用途说明"
                :rows="3"
                :maxLength="500"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="24">
            <a-form-model-item
              label="启用状态"
              prop="enabled"
              :label-col="{ span: 3 }"
              :wrapper-col="{ span: 21 }"
            >
              <a-select
                v-model="templateForm.enabled"
                placeholder="请选择启用状态"
                style="width: 100%"
              >
                <a-select-option
                  v-for="item in promptTemplateStatusOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>

    <a-modal
      :maskClosable="false"
      :title="versionModalTitle"
      :visible="versionModalVisible"
      :confirm-loading="versionConfirmLoading"
      ok-text="确认保存"
      cancel-text="取消"
      width="860px"
      destroy-on-close
      wrap-class-name="version-modal-wrap"
      @ok="handleVersionModalOk"
      @cancel="handleVersionModalCancel"
    >
      <a-form-model
        ref="versionForm"
        :model="versionForm"
        :rules="versionRulesComputed"
        layout="vertical"
        class="version-modal-form"
      >
        <a-row :gutter="24">
          <a-col v-if="versionModalType === 'edit'" :span="12">
            <a-form-model-item label="版本号">
              <a-input
                :value="formatVersionNo(versionForm.versionNo)"
                disabled
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="versionModalType === 'edit' ? 12 : 24">
            <a-form-model-item label="发布状态" prop="versionStatus" required>
              <a-select v-model="versionForm.versionStatus" placeholder="请选择发布状态">
                <a-select-option
                  v-for="item in promptVersionStatusOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-form-model-item label="提示词内容" prop="promptContent" required class="prompt-content-item">
          <a-textarea
            v-model="versionForm.promptContent"
            :placeholder="promptContentPlaceholder"
            :rows="9"
          />
          <div class="form-item-extra"><span v-pre>使用 {{varName}} 语法引用下方定义的变量</span></div>
        </a-form-model-item>

        <a-form-model-item label="备注">
          <a-textarea
            v-model="versionForm.remark"
            placeholder="本次变更说明"
            :rows="3"
            :maxLength="500"
          />
        </a-form-model-item>

        <a-form-model-item label="变量定义" class="variable-form-item">
          <div class="variable-section">
            <div class="variable-section__toolbar">
              <a-button type="dashed" size="small" @click="handleAddVariable">
                <a-icon type="plus" />添加变量
              </a-button>
            </div>

            <a-table
              :rowKey="(record) => record.uid"
              :columns="variableColumns"
              :data-source="versionForm.variables"
              :pagination="false"
              :locale="{ emptyText: '暂无变量，点击右上角添加' }"
              bordered
              size="middle"
              class="variable-table"
            >
              <template slot="varName" slot-scope="text, record">
                <a-input v-model.trim="record.name" placeholder="变量名" allow-clear />
              </template>
              <template slot="varType" slot-scope="text, record">
                <a-select v-model="record.type">
                  <a-select-option
                    v-for="item in promptVariableTypeOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </a-select-option>
                </a-select>
              </template>
              <template slot="varDefault" slot-scope="text, record">
                <a-input v-model="record.defaultValue" placeholder="默认值" allow-clear />
              </template>
              <template slot="varRequired" slot-scope="text, record">
                <a-checkbox v-model="record.required" />
              </template>
              <template slot="varDesc" slot-scope="text, record">
                <a-input v-model="record.description" placeholder="说明" allow-clear />
              </template>
              <template slot="varAction" slot-scope="text, record">
                <a-button
                  type="danger"
                  size="small"
                  icon="close"
                  class="variable-delete-btn"
                  @click="handleRemoveVariable(record)"
                />
              </template>
            </a-table>
          </div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import {
  getPromptTemplateList,
  createPromptTemplate,
  updatePromptTemplate,
  updatePromptTemplateStatus,
  deletePromptTemplate,
  createPromptVersion,
  updatePromptVersion,
  getPromptVersionList,
  deletePromptVersion,
} from '@/api/intelligentCustomerService';
import {
  PROMPT_TYPE_OPTIONS,
  PROMPT_TYPE_MAP,
  PROMPT_TEMPLATE_STATUS,
  PROMPT_VERSION_STATUS,
  PROMPT_VERSION_STATUS_MAP,
  PROMPT_VARIABLE_TYPE_OPTIONS,
} from '../help';

const getDefaultTemplateForm = () => ({
  id: undefined,
  key: '',
  name: '',
  promptType: 'SYSTEM',
  category: '',
  description: '',
  enabled: 1,
});

const getDefaultVariable = () => ({
  uid: `${Date.now()}_${Math.random()}`,
  name: '',
  type: 'string',
  defaultValue: '',
  required: false,
  description: '',
});

const getDefaultVersionForm = () => ({
  id: undefined,
  versionNo: '',
  versionStatus: 0,
  promptContent: '',
  remark: '',
  variables: [],
});

const mapVariableToForm = (variable) => ({
  uid: `${Date.now()}_${Math.random()}`,
  name: variable.name || '',
  type: variable.type || 'string',
  defaultValue: variable.defaultValue || '',
  required: !!variable.required,
  description: variable.description || '',
});

const mapVersionItem = (item) => ({
  id: item.id,
  templateId: item.templateId,
  versionNo: item.versionNo,
  promptContent: item.promptContent,
  variables: item.variables || [],
  modelConfig: item.modelConfig,
  versionStatus: item.versionStatus,
  versionStatusDesc: item.versionStatusDesc,
  remark: item.remark,
  creator: item.createdBy,
  publisher: item.publishBy,
  publishTime: item.publishTime,
  createTime: item.createTime,
  updateTime: item.updateTime,
});

const mapTemplateItem = (item) => ({
  id: item.id,
  name: item.promptName,
  key: item.promptKey,
  category: item.category,
  promptType: item.promptType,
  currentVersionId: item.currentVersionId,
  enabled: item.status === 1,
  status: item.status,
  description: item.description,
  updateTime: item.updateTime,
  createTime: item.createTime,
  createdBy: item.createdBy,
  updatedBy: item.updatedBy,
  versions: [],
});

export default {
  name: 'PromptManagement',
  data() {
    return {
      searchKeyword: '',
      filterType: '',
      selectedTemplateId: null,
      listLoading: false,
      versionLoading: false,
      searchTimer: null,
      typeOptions: [
        { label: '全部类型', value: '' },
        { label: '客服', value: '客服' },
        { label: '售前', value: '售前' },
        { label: '售后', value: '售后' },
      ],
      templates: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      versionPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      promptTypeOptions: PROMPT_TYPE_OPTIONS,
      promptTemplateStatusOptions: PROMPT_TEMPLATE_STATUS,
      promptVersionStatusOptions: PROMPT_VERSION_STATUS,
      promptVariableTypeOptions: PROMPT_VARIABLE_TYPE_OPTIONS,
      templateModalVisible: false,
      templateModalType: 'add',
      confirmLoading: false,
      versionModalVisible: false,
      versionModalType: 'add',
      versionConfirmLoading: false,
      versionForm: getDefaultVersionForm(),
      promptContentPlaceholder: '请输入完整的提示词内容，支持 {{变量名}} 占位符',
      variableColumns: [
        {
          title: '变量名',
          dataIndex: 'name',
          key: 'name',
          scopedSlots: { customRender: 'varName' },
        },
        {
          title: '类型',
          dataIndex: 'type',
          key: 'type',
          width: 120,
          scopedSlots: { customRender: 'varType' },
        },
        {
          title: '默认值',
          dataIndex: 'defaultValue',
          key: 'defaultValue',
          scopedSlots: { customRender: 'varDefault' },
        },
        {
          title: '必填',
          dataIndex: 'required',
          key: 'required',
          width: 72,
          align: 'center',
          scopedSlots: { customRender: 'varRequired' },
        },
        {
          title: '说明',
          dataIndex: 'description',
          key: 'description',
          scopedSlots: { customRender: 'varDesc' },
        },
        {
          title: '操作',
          key: 'action',
          width: 64,
          align: 'center',
          scopedSlots: { customRender: 'varAction' },
        },
      ],
      templateForm: getDefaultTemplateForm(),
      templateRules: {
        key: [
          { required: true, message: '请输入业务Key', trigger: 'blur' },
          {
            pattern: /^[a-z][a-z0-9_]*$/,
            message: '仅支持小写字母、数字和下划线，且以字母开头',
            trigger: 'blur',
          },
        ],
        name: [{ required: true, message: '请输入模板名称', trigger: 'blur' }],
        promptType: [{ required: true, message: '请选择提示词类型', trigger: 'change' }],
        category: [{ required: true, message: '请输入业务分类', trigger: 'blur' }],
        enabled: [{ required: true, message: '请选择启用状态', trigger: 'change' }],
      },
    };
  },
  computed: {
    templateModalTitle() {
      return this.templateModalType === 'edit' ? '编辑提示词模板' : '新增提示词模板';
    },
    selectedTemplate() {
      return this.templates.find((item) => item.id === this.selectedTemplateId) || null;
    },
    versionModalTitle() {
      return this.versionModalType === 'edit' ? '编辑版本' : '新增版本';
    },
    versionRulesComputed() {
      return {
        versionStatus: [{ required: true, message: '请选择发布状态', trigger: 'change' }],
        promptContent: [{ required: true, message: '请输入提示词内容', trigger: 'blur' }],
      };
    },
  },
  watch: {
    searchKeyword() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.handleSearch();
      }, 300);
    },
  },
  mounted() {
    this.fetchTemplateList();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    isSuccess(res) {
      return res && [0, '0', 200, '200'].includes(res.code);
    },
    formatPromptType(type) {
      return PROMPT_TYPE_MAP[type] || type || '-';
    },
    formatVersionNo(versionNo) {
      if (versionNo === undefined || versionNo === null || versionNo === '') return '-';
      const text = String(versionNo);
      return text.startsWith('v') ? text : `v${text}`;
    },
    formatVersionStatus(status) {
      return PROMPT_VERSION_STATUS_MAP[status]?.label || '-';
    },
    getVersionStatusColor(status) {
      return PROMPT_VERSION_STATUS_MAP[status]?.color || 'default';
    },
    isCurrentVersion(version) {
      return this.selectedTemplate?.currentVersionId === version.id;
    },
    handleSearch() {
      this.pagination.current = 1;
      this.fetchTemplateList();
    },
    handlePageChange(page) {
      this.pagination.current = page;
      this.fetchTemplateList();
    },
    async fetchTemplateList() {
      this.listLoading = true;
      try {
        const params = {
          pageNo: this.pagination.current,
          pageSize: this.pagination.pageSize,
        };
        if (this.searchKeyword) {
          params.promptName = this.searchKeyword;
        }
        if (this.filterType) {
          params.category = this.filterType;
        }
        const res = await getPromptTemplateList(params);
        if (this.isSuccess(res)) {
          const list = res.data?.list || res.data?.records || [];
          this.templates = list.map(mapTemplateItem);
          this.pagination.total = res.data?.total || 0;
          await this.syncSelectedTemplate();
        } else {
          this.$message.error(res?.msg || '获取模板列表失败');
        }
      } catch {
        this.$message.error('获取模板列表失败');
      } finally {
        this.listLoading = false;
      }
    },
    async syncSelectedTemplate() {
      const exists = this.templates.some((item) => item.id === this.selectedTemplateId);
      if (!exists) {
        this.selectedTemplateId = this.templates.length ? this.templates[0].id : null;
      }
      if (this.selectedTemplateId) {
        this.versionPagination.current = 1;
        await this.fetchVersionList();
      }
    },
    selectTemplate(id) {
      this.selectedTemplateId = id;
      this.versionPagination.current = 1;
      this.fetchVersionList();
    },
    handleVersionPageChange(page) {
      this.versionPagination.current = page;
      this.fetchVersionList();
    },
    async fetchVersionList() {
      if (!this.selectedTemplateId) return;
      const templateId = this.selectedTemplateId;
      this.versionLoading = true;
      try {
        const params = {
          templateId,
          pageNo: this.versionPagination.current,
          pageSize: this.versionPagination.pageSize,
        };
        const res = await getPromptVersionList(params);
        if (this.isSuccess(res)) {
          const list = res.data?.list || res.data?.records || [];
          const templateIndex = this.templates.findIndex((item) => item.id === templateId);
          if (templateIndex > -1) {
            this.$set(
              this.templates[templateIndex],
              'versions',
              list.map(mapVersionItem)
            );
          }
          this.versionPagination.total = res.data?.total || 0;
        } else {
          this.$message.error(res?.msg || '获取版本列表失败');
        }
      } catch {
        this.$message.error('获取版本列表失败');
      } finally {
        this.versionLoading = false;
      }
    },
    resetTemplateForm() {
      this.templateForm = getDefaultTemplateForm();
      if (this.$refs.templateForm) {
        this.$refs.templateForm.resetFields();
      }
    },
    handleAddTemplate() {
      this.templateModalType = 'add';
      this.resetTemplateForm();
      this.templateModalVisible = true;
    },
    handleTemplateModalCancel() {
      this.templateModalVisible = false;
      this.templateModalType = 'add';
      this.resetTemplateForm();
    },
    handleTemplateModalOk() {
      this.$refs.templateForm.validate(async (valid) => {
        if (!valid) return;
        this.confirmLoading = true;
        try {
          const payload = {
            promptKey: this.templateForm.key,
            promptName: this.templateForm.name,
            promptType: this.templateForm.promptType,
            category: this.templateForm.category,
            description: this.templateForm.description,
            status: this.templateForm.enabled,
          };
          const isEdit = this.templateModalType === 'edit';
          if (isEdit) {
            payload.id = this.templateForm.id;
          }
          const api = isEdit ? updatePromptTemplate : createPromptTemplate;
          const res = await api(payload);
          if (this.isSuccess(res) && res.data) {
            this.$message.success(res.msg || '保存成功');
            const selectedId = isEdit ? this.templateForm.id : null;
            const promptKey = payload.promptKey;
            this.handleTemplateModalCancel();
            if (!isEdit) {
              this.pagination.current = 1;
            }
            await this.fetchTemplateList();
            if (isEdit) {
              this.selectedTemplateId = selectedId;
            } else {
              const createdTemplate = this.templates.find((item) => item.key === promptKey);
              if (createdTemplate) {
                this.selectedTemplateId = createdTemplate.id;
              }
            }
            await this.fetchVersionList();
          } else {
            this.$message.error(res?.msg || '保存失败');
          }
        } catch {
          this.$message.error('保存失败');
        } finally {
          this.confirmLoading = false;
        }
      });
    },
    handleEditTemplate() {
      if (!this.selectedTemplate) return;
      this.templateModalType = 'edit';
      this.templateForm = {
        id: this.selectedTemplate.id,
        key: this.selectedTemplate.key,
        name: this.selectedTemplate.name,
        promptType: this.selectedTemplate.promptType,
        category: this.selectedTemplate.category,
        description: this.selectedTemplate.description || '',
        enabled: this.selectedTemplate.status ?? (this.selectedTemplate.enabled ? 1 : 0),
      };
      this.templateModalVisible = true;
    },
    handleToggleTemplate() {
      if (!this.selectedTemplate) return;
      const nextStatus = this.selectedTemplate.enabled ? 0 : 1;
      const actionText = nextStatus === 1 ? '启用' : '禁用';
      const templateId = this.selectedTemplateId;
      const that = this;
      this.$confirm({
        title: `确认${actionText}该提示词模板？`,
        onOk() {
          return updatePromptTemplateStatus(templateId, nextStatus).then((res) => {
            if (that.isSuccess(res)) {
              that.$message.success(res.msg || `${actionText}成功`);
              return that.fetchTemplateList();
            }
            that.$message.error(res?.msg || `${actionText}失败`);
          });
        },
      });
    },
    handleDeleteTemplate() {
      if (!this.selectedTemplate) return;
      const templateId = this.selectedTemplateId;
      const that = this;
      this.$confirm({
        title: '确认删除该提示词模板？',
        content: '删除后不可恢复，请谨慎操作',
        okType: 'danger',
        onOk() {
          return deletePromptTemplate(templateId).then(async (res) => {
            if (that.isSuccess(res) && res.data) {
              that.$message.success(res.msg || '删除成功');
              if (that.templates.length === 1 && that.pagination.current > 1) {
                that.pagination.current -= 1;
              }
              await that.fetchTemplateList();
            } else {
              that.$message.error(res?.msg || '删除失败');
            }
          });
        },
      });
    },
    handleAddVersion() {
      if (!this.selectedTemplate) return;
      this.versionModalType = 'add';
      this.resetVersionForm();
      this.versionForm.variables = [getDefaultVariable()];
      this.versionModalVisible = true;
    },
    resetVersionForm() {
      this.versionForm = getDefaultVersionForm();
      if (this.$refs.versionForm) {
        this.$refs.versionForm.resetFields();
      }
    },
    handleVersionModalCancel() {
      this.versionModalVisible = false;
      this.versionModalType = 'add';
      this.resetVersionForm();
    },
    handleAddVariable() {
      this.versionForm.variables.push(getDefaultVariable());
    },
    handleRemoveVariable(record) {
      const index = this.versionForm.variables.findIndex((item) => item.uid === record.uid);
      if (index > -1) {
        this.versionForm.variables.splice(index, 1);
      }
    },
    validateVariables() {
      const variables = this.versionForm.variables.filter((item) => item.name);
      const names = variables.map((item) => item.name);
      const duplicateName = names.find((name, index) => names.indexOf(name) !== index);
      if (duplicateName) {
        this.$message.error(`变量名「${duplicateName}」重复，请修改后重试`);
        return false;
      }
      const invalidRow = this.versionForm.variables.find(
        (item) => !item.name && (item.defaultValue || item.description)
      );
      if (invalidRow) {
        this.$message.error('请填写变量名，或删除未完整填写的变量行');
        return false;
      }
      return true;
    },
    buildVersionPayload() {
      const variables = this.versionForm.variables
        .filter((item) => item.name)
        .map(({ name, type, required, defaultValue, description }) => ({
          name,
          type,
          required,
          defaultValue,
          description,
        }));
      const payload = {
        templateId: this.selectedTemplateId,
        promptContent: this.versionForm.promptContent,
        versionStatus: this.versionForm.versionStatus,
        remark: this.versionForm.remark,
        variables,
      };
      if (this.versionModalType === 'edit') {
        payload.id = this.versionForm.id;
      }
      return payload;
    },
    handleVersionModalOk() {
      this.$refs.versionForm.validate(async (valid) => {
        if (!valid || !this.validateVariables()) return;
        this.versionConfirmLoading = true;
        try {
          const isEdit = this.versionModalType === 'edit';
          const api = isEdit ? updatePromptVersion : createPromptVersion;
          const res = await api(this.buildVersionPayload());
          if (this.isSuccess(res) && res.data) {
            this.$message.success(res.msg || '保存成功');
            this.handleVersionModalCancel();
            await this.fetchTemplateList();
          } else {
            this.$message.error(res?.msg || '保存失败');
          }
        } catch {
          this.$message.error('保存失败');
        } finally {
          this.versionConfirmLoading = false;
        }
      });
    },
    handleTestRecords() {
      this.$message.info('测试记录（待接入）');
    },
    handleEditVersion(version) {
      if (!version || !this.selectedTemplate) return;
      this.versionModalType = 'edit';
      this.versionForm = {
        id: version.id,
        versionNo: version.versionNo ?? '',
        versionStatus: version.versionStatus ?? 0,
        promptContent: version.promptContent || '',
        remark: version.remark || '',
        variables: (version.variables || []).map(mapVariableToForm),
      };
      this.versionModalVisible = true;
    },
    handleDeleteVersion(version) {
      if (!version || !version.id) return;
      const versionId = version.id;
      const that = this;
      this.$confirm({
        title: '确认删除该版本？',
        content: '删除后不可恢复，请谨慎操作',
        okType: 'danger',
        onOk() {
          return deletePromptVersion(versionId).then(async (res) => {
            if (that.isSuccess(res) && res.data) {
              that.$message.success(res.msg || '删除成功');
              if (
                that.selectedTemplate?.versions?.length === 1 &&
                that.versionPagination.current > 1
              ) {
                that.versionPagination.current -= 1;
              }
              const versionPage = that.versionPagination.current;
              const selectedId = that.selectedTemplateId;
              await that.fetchTemplateList();
              that.selectedTemplateId = selectedId;
              that.versionPagination.current = versionPage;
              await that.fetchVersionList();
            } else {
              that.$message.error(res?.msg || '删除失败');
            }
          });
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.prompt-management {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
  overflow: hidden;

  .prompt-layout {
    display: flex;
    flex: 1;
    gap: 16px;
    margin-top: 16px;
    min-height: 0;
    overflow: hidden;
  }

  .template-sidebar {
    width: 360px;
    flex-shrink: 0;
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }

  .sidebar-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  .sidebar-filters {
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }

  .search-input,
  .type-select {
    width: 100%;
  }

  .template-list-spin {
    flex: 1;
    min-height: 0;
    overflow: hidden;

    ::v-deep .ant-spin-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

  .template-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px;
  }

  .sidebar-pagination {
    flex-shrink: 0;
    padding: 8px 12px;
    border-top: 1px solid #f0f0f0;
    text-align: center;
  }

  .template-item {
    padding: 12px;
    margin-bottom: 4px;
    border-radius: 4px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f5f5f5;
    }

    &--active {
      background: #e6f7ff;
      border-color: #91d5ff;
      border-left: 3px solid #1890ff;
      padding-left: 10px;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;
    }

    &__name {
      font-weight: 600;
      font-size: 14px;
      color: rgba(0, 0, 0, 0.85);
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-right: 8px;
    }

    &__status {
      flex-shrink: 0;
      margin: 0;
    }

    &__desc {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.45);
      margin-bottom: 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__meta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      font-size: 12px;
    }

    &__key {
      background: #f5f5f5;
      border: 1px solid #e8e8e8;
      border-radius: 2px;
      padding: 0 6px;
      color: rgba(0, 0, 0, 0.65);
      font-family: monospace;
      font-size: 11px;
    }

    &__version {
      margin: 0;
      font-size: 11px;
    }

    &__category {
      color: rgba(0, 0, 0, 0.45);

      .anticon {
        margin-right: 4px;
      }
    }
  }

  .template-empty {
    margin-top: 40px;
  }

  .template-detail {
    flex: 1;
    min-height: 0;
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .detail-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0;
  }

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    flex-shrink: 0;
  }

  .detail-header__title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }

  .detail-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  .detail-header__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .meta-item {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);

    .anticon {
      margin-right: 6px;
    }
  }

  .detail-header__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    flex-shrink: 0;
  }

  .detail-description {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 16px;
    background: #fafafa;
    border-radius: 4px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    margin-bottom: 24px;
    flex-shrink: 0;

    .desc-icon {
      margin-top: 2px;
      color: rgba(0, 0, 0, 0.45);
    }
  }

  .version-section {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-right: 4px;
  }

  .version-empty {
    margin: 24px 0;
  }

  .version-pagination {
    margin-top: 16px;
    text-align: right;
  }

  .version-section__title {
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 16px;

    .anticon {
      margin-right: 8px;
      color: rgba(0, 0, 0, 0.45);
    }
  }

  .version-card {
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
    background: #fff;

    &:last-child {
      margin-bottom: 0;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    &__header-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__time {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.45);
    }

    &__meta {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 16px;
      margin-bottom: 12px;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.45);
    }

    &__remark {
      color: rgba(0, 0, 0, 0.65);
    }

    &__content {
      background: #fafafa;
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      padding: 12px 16px;
      margin-bottom: 12px;
    }

    &__variables {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 12px;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 4px;
      border-top: 1px solid #f0f0f0;
      padding-top: 12px;
    }
  }

  .version-number {
    font-size: 15px;
    font-weight: 600;
    color: #1890ff;
  }

  .prompt-content {
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
    color: rgba(0, 0, 0, 0.75);
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
  }

  .variables-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    margin-right: 4px;
  }

  .variable-tag {
    margin: 0;
    font-family: monospace;
    font-size: 12px;
  }

  .required-mark {
    color: #ff4d4f;
    margin-left: 1px;
  }

  .danger-btn {
    color: #ff4d4f;

    &:hover {
      color: #ff7875;
    }
  }

  .template-modal-form {
    .ant-form-item {
      margin-bottom: 16px;
    }

    .ant-form-item-with-help {
      margin-bottom: 0;
    }
  }

  .version-modal-form {
    .ant-form-item {
      margin-bottom: 20px;
      padding-bottom: 0;
    }

    .ant-form-item-label > label {
      color: rgba(0, 0, 0, 0.85);
      font-weight: 500;
    }

    .ant-select,
    .ant-input,
    .ant-input-number {
      width: 100%;
    }

    .prompt-content-item {
      margin-bottom: 20px;
    }

    .form-item-extra {
      margin-top: 6px;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.45);
      line-height: 1.5;
    }
  }

  .variable-form-item {
    margin-bottom: 0;

    .ant-form-item-control {
      line-height: 1;
    }
  }

  .variable-section {
    width: 100%;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 12px 12px 16px;
    background: #fff;
  }

  .variable-section__toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  .variable-table {
    .ant-table-thead > tr > th {
      background: #fafafa;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.65);
    }

    .ant-table-tbody > tr > td {
      vertical-align: middle;
      padding: 10px 12px;
    }

    .ant-select,
    .ant-input {
      width: 100%;
    }
  }

  .variable-delete-btn {
    width: 28px;
    min-width: 28px;
    height: 28px;
    padding: 0;
    line-height: 26px;
  }
}
</style>

<style lang="scss">
.version-modal-wrap {
  .ant-modal-body {
    max-height: calc(100vh - 220px);
    overflow-y: auto;
    padding: 20px 24px 8px;
  }

  .ant-modal-footer {
    padding: 12px 24px 20px;
    border-top: 1px solid #f0f0f0;
  }
}
</style>
